// ///////////////////////////////////////////////////////
// ///////////// The Radar Chart Function ////////////////
// ///////////// Written by Nadieh Bremer ////////////////
// //////////////// VisualCinnamon.com ///////////////////
// ///////// Inspired by the code of alangrafu ///////////
// ///////////////////////////////////////////////////////

import * as d3 from "d3";
import router from "../router";
import i18n from "../lang/lang";

export function RadarChart(id, data, pillarID, options) {
  var cfg = {
    w: 600, // Width of the circle
    h: 600, // Height of the circle
    margin: { top: 20, right: 50, bottom: 0, left: 50 }, // The margins of the SVG
    levels: 4, // How many levels or inner circles should there be drawn
    maxValue: 0, // What is the value that the biggest circle will represent
    labelFactor: 1.1, // How much farther than the radius of the outer circle should the labels be placed
    wrapWidth: 50, // The number of pixels after which a label needs to be given a new line
    opacityArea: 0.35, // The opacity of the area of the blob
    dotRadius: 4, // The size of the colored circles of each blog
    opacityCircles: 0.1, // The opacity of the circles of each blob
    strokeWidth: 2, // The width of the stroke around each blob
    roundStrokes: false, // If true the area and stroke will follow a round path (cardinal-closed)
    color: d3.scaleOrdinal(d3.schemeCategory10), // Color function
  };

  // Put all of the options into a variable called cfg
  if (typeof options !== "undefined") {
    for (var i in options) {
      if (typeof options[i] !== "undefined") {
        cfg[i] = options[i];
      }
    } // for i
  } // if

  cfg["x1"] = cfg.w / 2; // Canvas limit in right
  cfg["x0"] = -cfg.x1; // Canvas limit in left
  cfg["y1"] = cfg.h / 2; // Canvas limit in top
  cfg["y0"] = -cfg.y1; // Canvas limit in bottom

  var fontSize = Math.round(cfg.w / 14);
  fontSize = fontSize > 14 ? 14 : fontSize;
  // If the supplied maxValue is smaller than the actual one, replace by the max in the data
  var maxValue = Math.max(cfg.maxValue, 100);

  var allAxis = data[0].map(function (i) {
    return [i.acronym, i.index, i.id, i.name];
  }); // Names of each axis
  var total = allAxis.length; // The number of different axes
  var radius = Math.min(
    (cfg.w - cfg.margin.left - cfg.margin.right) / 2,
    (cfg.h - cfg.margin.top - cfg.margin.bottom) / 2
  ); // Radius of the outermost circle
  var Format = function (d) {
    return parseInt(d) + "%";
  };
  var angleSlice = (Math.PI * 2) / total; // The width in radians of each "slice"
  // Scale for the radius
  var rScale = d3.scaleLinear().range([0, radius]).domain([0, maxValue]);

  // ///////////////////////////////////////////////////////
  // ////////// Create the container SVG and g /////////////
  // ///////////////////////////////////////////////////////

  // Remove whatever chart with the same id/class was present before
  d3.select(id).select("svg").remove();

  // Initiate the radar chart SVG
  var svg = d3
    .select(id)
    .append("svg")
    .attr("width", cfg.w)
    .attr("height", cfg.h)
    .attr("class", "radar" + id);

  // Append a g element
  var g = svg
    .append("g")
    .attr("transform", "translate(" + cfg.w / 2 + "," + cfg.h / 2 + ")");

  // ///////////////////////////////////////////////////////
  // //////// Glow filter for some extra pizzazz ///////////
  // ///////////////////////////////////////////////////////

  // Filter for the outside glow
  /* var filter = g.append('defs').append('filter').attr('id', 'glow'),
    feGaussianBlur = filter.append('feGaussianBlur').attr('stdDeviation', '2.5').attr('result', 'coloredBlur'),
    feMerge = filter.append('feMerge'),
    feMergeNode_1 = feMerge.append('feMergeNode').attr('in', 'coloredBlur'),
    feMergeNode_2 = feMerge.append('feMergeNode').attr('in', 'SourceGraphic') */

  // ///////////////////////////////////////////////////////
  // ///////////// Draw the Circular grid //////////////////
  // ///////////////////////////////////////////////////////

  // Wrapper for the grid & axes
  var axisGrid = g.append("g").attr("class", "axisWrapper");

  var zone = ["positive", "intermediate", "negative"];
  var colours = ["#009355 ", "#ffbe00", "#e60246"];

  // Draw the background circles
  axisGrid
    .selectAll(".levels")
    .data(d3.range(1, cfg.levels + 1).reverse())
    .enter()
    .append("circle")
    .attr("class", function (d, i) {
      return "gridCircle " + zone[i];
    })
    .attr("r", function (d) {
      return (radius / cfg.levels) * d;
    })
    .style("fill", "#CDCDCD")
    .style("fill-opacity", cfg.opacityCircles)
    // .style('filter', 'url(#glow)')
    .style("stroke", function (d, i) {
      return colours[i];
    });

  // Text indicating at what % each level is
  axisGrid
    .selectAll(".axisLabel")
    .data(d3.range(1, cfg.levels + 1).reverse())
    .enter()
    .append("text")
    .attr("class", "axisLabel")
    .attr("x", 4)
    .attr("y", function (d) {
      return (-d * radius) / cfg.levels;
    })
    .attr("dy", "0.4em")
    .style("font-size", fontSize * 0.8 + "px")
    .style("font-family", "Montserrat-Regular")
    .attr("fill", "#737373")
    .text(function (d) {
      return Format((maxValue * d) / cfg.levels);
    });

  // ///////////////////////////////////////////////////////
  // ////////////////// Draw the axes //////////////////////
  // ///////////////////////////////////////////////////////

  // Create the straight lines radiating outward from the center
  var axis = axisGrid
    .selectAll(".axis")
    .data(allAxis)
    .enter()
    .append("g")
    .attr("class", "axis");
  // Append the lines
  axis
    .append("line")
    .attr("x1", 0)
    .attr("y1", 0)
    .attr("x2", function (d, i) {
      return rScale(maxValue * 1.1) * Math.cos(angleSlice * i - Math.PI / 2);
    })
    .attr("y2", function (d, i) {
      return rScale(maxValue * 1.1) * Math.sin(angleSlice * i - Math.PI / 2);
    })
    .attr("class", "line")
    .style("stroke", "white")
    .style("stroke-width", "2px");

  // Append the labels at each axis
  axis
    .append("text")
    .style("font-size", "15px")
    .style("cursor", "pointer")
    .attr("text-anchor", "middle")
    .style("font-family", "Montserrat-Regular")
    .attr("dy", "0rem")
    .attr("x", function (d, i) {
      return (
        rScale((maxValue + 14) * cfg.labelFactor) *
        Math.cos(angleSlice * i - Math.PI / 2)
      );
    })
    .attr("y", function (d, i) {
      return (
        rScale((maxValue + 14) * cfg.labelFactor) *
        Math.sin(angleSlice * i - Math.PI / 2)
      );
    })
    .text(function (d) {
      return i18n.tc(d[0]) + " (" + Format(d[1]) + ")";
    })
    .call(wrap, cfg.wrapWidth, false)
    .on("click", (d) => {
      router.push("/reports/gender-gap/" + pillarID + "/" + d[2]);
    })
    .on("mouseover", function (d) {
      var newX = parseFloat(d3.select(this).attr("x"));
      var newY = parseFloat(d3.select(this).attr("y"));
      tooltipBox.transition().duration(200).style("opacity", 1);
      tooltipBox
        .append("text")
        .attr("class", "tooltip-text")
        .attr("stroke", "none")
        .style("fill", "white")
        .style("text-anchor", "middle")
        .style("font-family", "Montserrat-Regular")
        .style("pointer-events", "none")
        .style("font-size", fontSize * 0.9 + "px")
        .attr("x", newX)
        .attr("y", newY)
        .attr("dy", 0)
        .text(i18n.tc(d[3]))
        .call(wrap, 200, true, true);
    })
    .on("mouseout", function () {
      tooltipBox.transition().duration(200).style("opacity", 0);
      tooltipBox.select(".text-background").remove();
      tooltipBox.select(".tooltip-text").remove();
    });

  // ///////////////////////////////////////////////////////
  // /////////// Draw the radar chart blobs ////////////////
  // ///////////////////////////////////////////////////////

  // The radial line function
  var radarLine = d3
    .lineRadial()
    .curve(d3.curveLinearClosed)
    .radius(function (d) {
      return rScale(d.index);
    })
    .angle(function (d, i) {
      return i * angleSlice;
    });

  if (cfg.roundStrokes) {
    radarLine.curve(d3.curveCardinalClosed);
  }

  // Create a wrapper for the blobs
  var blobWrapper = g
    .selectAll(".radarWrapper")
    .data(data)
    .enter()
    .append("g")
    .attr("class", "radarWrapper");

  // Append the backgrounds
  blobWrapper
    .append("path")
    .attr("class", "radarArea")
    .attr("d", function (d) {
      return radarLine(d);
    })
    .style("fill", function (d, i) {
      return cfg.color(i);
    })
    .style("fill-opacity", cfg.opacityArea)
    .on("mouseover", function () {
      // Dim all blobs
      d3.selectAll(".radarArea")
        .transition()
        .duration(200)
        .style("fill-opacity", 0.1);
      // Bring back the hovered over blob
      d3.select(this).transition().duration(200).style("fill-opacity", 0.7);
    })
    .on("mouseout", function () {
      // Bring back all blobs
      d3.selectAll(".radarArea")
        .transition()
        .duration(200)
        .style("fill-opacity", cfg.opacityArea);
    });

  // Create the outlines
  blobWrapper
    .append("path")
    .attr("class", "radarStroke")
    .attr("d", function (d) {
      return radarLine(d);
    })
    .style("stroke-width", cfg.strokeWidth + "px")
    .style("stroke", function (d, i) {
      return cfg.color(i);
    })
    .style("fill", "none")
    .style("filter", "url(#glow)");

  // Append the circles
  blobWrapper
    .selectAll(".radarCircle")
    .data(function (d) {
      return d;
    })
    .enter()
    .append("circle")
    .attr("class", "radarCircle")
    .attr("r", cfg.dotRadius)
    .attr("cx", function (d, i) {
      return rScale(d.index) * Math.cos(angleSlice * i - Math.PI / 2);
    })
    .attr("cy", function (d, i) {
      return rScale(d.index) * Math.sin(angleSlice * i - Math.PI / 2);
    })
    .style("fill", function (d, i, j) {
      return cfg.color(j);
    })
    .style("fill-opacity", 0.8);

  // ///////////////////////////////////////////////////////
  // ////// Append invisible circles for tooltip ///////////
  // ///////////////////////////////////////////////////////

  // Wrapper for the invisible circles on top
  var blobCircleWrapper = g
    .selectAll(".radarCircleWrapper")
    .data(data)
    .enter()
    .append("g")
    .attr("class", "radarCircleWrapper");

  // Append a set of invisible circles on top for the mouseover pop-up
  blobCircleWrapper
    .selectAll(".radarInvisibleCircle")
    .data(function (d) {
      return d;
    })
    .enter()
    .append("circle")
    .attr("class", "radarInvisibleCircle")
    .attr("r", cfg.dotRadius * 1.5)
    .attr("cx", function (d, i) {
      return rScale(d.value) * Math.cos(angleSlice * i - Math.PI / 2);
    })
    .attr("cy", function (d, i) {
      return rScale(d.value) * Math.sin(angleSlice * i - Math.PI / 2);
    })
    .style("fill", "none")
    .style("pointer-events", "all")
    .on("mouseover", function (d) {
      var newX = parseFloat(d3.select(this).attr("cx"));
      var newY = parseFloat(d3.select(this).attr("cy")) + 25;
      tooltipBox.transition().duration(200).style("opacity", 1);
      tooltipBox
        .append("text")
        .attr("class", "tooltip-text")
        .attr("stroke", "none")
        .style("fill", "white")
        .style("text-anchor", "middle")
        .style("font-family", "Montserrat-Regular")
        .attr("x", newX)
        .attr("y", newY)
        .attr("dy", 0)
        .text(Format(d.index))
        .call(wrap, 60, true, true);
    })
    .on("mouseout", function () {
      tooltipBox.transition().duration(200).style("opacity", 0);
      tooltipBox.select(".text-background").remove();
      tooltipBox.select(".tooltip-text").remove();
    });

  var tooltipBox = g
    .append("g")
    .attr("class", "tooltip-box")
    .style("opacity", 0);

  // ///////////////////////////////////////////////////////
  // ///////////////// Helper Function /////////////////////
  // ///////////////////////////////////////////////////////

  // Taken from http://bl.ocks.org/mbostock/7555321
  // Wraps SVG text
  function wrap(text, width, background) {
    // isTooltip = isTooltip || false
    text.each(function () {
      var text = d3.select(this);
      var rect = d3.select(this.parentNode); // Is used in tooltips
      d3.select(this.parentNode).select(".text-background").remove();
      var words = text.text().split(/\s+/).reverse();
      var word;
      var line = [];
      var lines = [];
      var lineNumber = 0;
      var lineHeight = 1; // ems
      var y = text.attr("y");
      var x = text.attr("x");

      var dy = parseFloat(text.attr("dy"));
      var tspan = text
        .text(null)
        .append("tspan")
        .attr("x", x)
        .attr("y", y)
        .attr("dy", dy + "em")
        .attr("z-index", "100");

      while (word = words.pop()) {// eslint-disable-line
        line.push(word);
        tspan.text(line.join(" "));
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(" "));
          lines.push(line);
          line = [word];
          tspan = text
            .append("tspan")
            .attr("x", x)
            .attr("y", y)
            .attr("dy", ++lineNumber * lineHeight + dy + "em")
            .text(word);
        }
      }

      var textBox = tspan.node().getBBox();
      let boundingSquare = text.node().getBoundingClientRect();
      var tBackground = rect.append("rect");
      if (background) {
        tBackground
          .attr("class", "text-background")
          .attr("width", boundingSquare.width + 20)
          .attr("height", boundingSquare.height)
          .style("pointer-events", "none")
          .style("z-index", 0)
          .attr("fill", "black")
          .lower();
      }

      // Validamos los datos del tooltip para saber si se sale
      // Primero en x
      var deltaX = 0;
      var deltaY = 0;
      if (textBox.x < cfg.x0) {
        deltaX = cfg.x0 - textBox.x + 3;
      } else if (textBox.x + boundingSquare.width > cfg.x1) {
        deltaX = cfg.x1 - (textBox.x + boundingSquare.width) - 3;
      }
      // Luego en Y
      if (textBox.y + boundingSquare.height > cfg.y1) {
        deltaY = cfg.y1 - (textBox.y + boundingSquare.height) - 3;
      }

      if (background && this.childNodes) {
        this.childNodes.forEach((child) => {
          d3.select(child)
            .attr("x", deltaX / 4)
            .attr("y", deltaY / 4);
        });
        const container = d3.select(this.parentNode).node().getBBox();
        tBackground.attr("x", container.x - 10).attr("y", container.y);
      }
    });
  } // wrap
} // RadarChart

export function RadarChartConsolidated(id, data, pillarID, options) {
  var cfg = {
    w: 600, // Width of the circle
    h: 600, // Height of the circle
    margin: { top: 20, right: 50, bottom: 0, left: 50 }, // The margins of the SVG
    levels: 4, // How many levels or inner circles should there be drawn
    maxValue: 0, // What is the value that the biggest circle will represent
    labelFactor: 1.1, // How much farther than the radius of the outer circle should the labels be placed
    wrapWidth: 50, // The number of pixels after which a label needs to be given a new line
    opacityArea: 0.35, // The opacity of the area of the blob
    dotRadius: 4, // The size of the colored circles of each blog
    opacityCircles: 0.1, // The opacity of the circles of each blob
    strokeWidth: 2, // The width of the stroke around each blob
    roundStrokes: false, // If true the area and stroke will follow a round path (cardinal-closed)
    color: d3.scaleOrdinal(d3.schemeCategory10), // Color function
  };

  // Put all of the options into a variable called cfg
  if (typeof options !== "undefined") {
    for (var i in options) {
      if (typeof options[i] !== "undefined") {
        cfg[i] = options[i];
      }
    } // for i
  } // if

  cfg["x1"] = cfg.w / 2; // Canvas limit in right
  cfg["x0"] = -cfg.x1; // Canvas limit in left
  cfg["y1"] = cfg.h / 2; // Canvas limit in top
  cfg["y0"] = -cfg.y1; // Canvas limit in bottom

  var fontSize = Math.round(cfg.w / 14);
  fontSize = fontSize > 14 ? 14 : fontSize;
  // If the supplied maxValue is smaller than the actual one, replace by the max in the data
  var maxValue = Math.max(cfg.maxValue, 100);

  var allAxis = data[0].map(function (i) {
    return [i.acronym, i.index, i.id, i.name];
  }); // Names of each axis
  var total = allAxis.length; // The number of different axes
  var radius = Math.min(
    (cfg.w - cfg.margin.left - cfg.margin.right) / 2,
    (cfg.h - cfg.margin.top - cfg.margin.bottom) / 2
  ); // Radius of the outermost circle
  var Format = function (d) {
    return parseInt(d) + "%";
  };
  var angleSlice = (Math.PI * 2) / total; // The width in radians of each "slice"
  // Scale for the radius
  var rScale = d3.scaleLinear().range([0, radius]).domain([0, maxValue]);

  // ///////////////////////////////////////////////////////
  // ////////// Create the container SVG and g /////////////
  // ///////////////////////////////////////////////////////

  // Remove whatever chart with the same id/class was present before
  d3.select(id).select("svg").remove();

  // Initiate the radar chart SVG
  var svg = d3
    .select(id)
    .append("svg")
    .attr("width", cfg.w)
    .attr("height", cfg.h)
    .attr("class", "radar" + id);

  // Append a g element
  var g = svg
    .append("g")
    .attr("transform", "translate(" + cfg.w / 2 + "," + cfg.h / 2 + ")");

  // ///////////////////////////////////////////////////////
  // //////// Glow filter for some extra pizzazz ///////////
  // ///////////////////////////////////////////////////////

  // Filter for the outside glow
  /* var filter = g.append('defs').append('filter').attr('id', 'glow'),
    feGaussianBlur = filter.append('feGaussianBlur').attr('stdDeviation', '2.5').attr('result', 'coloredBlur'),
    feMerge = filter.append('feMerge'),
    feMergeNode_1 = feMerge.append('feMergeNode').attr('in', 'coloredBlur'),
    feMergeNode_2 = feMerge.append('feMergeNode').attr('in', 'SourceGraphic') */

  // ///////////////////////////////////////////////////////
  // ///////////// Draw the Circular grid //////////////////
  // ///////////////////////////////////////////////////////

  // Wrapper for the grid & axes
  var axisGrid = g.append("g").attr("class", "axisWrapper");

  var zone = ["positive", "intermediate", "negative"];
  var colours = ["#009355 ", "#ffbe00", "#e60246"];

  // Draw the background circles
  axisGrid
    .selectAll(".levels")
    .data(d3.range(1, cfg.levels + 1).reverse())
    .enter()
    .append("circle")
    .attr("class", function (d, i) {
      return "gridCircle " + zone[i];
    })
    .attr("r", function (d) {
      return (radius / cfg.levels) * d;
    })
    .style("fill", "#CDCDCD")
    .style("fill-opacity", cfg.opacityCircles)
    // .style('filter', 'url(#glow)')
    .style("stroke", function (d, i) {
      return colours[i];
    });

  // Text indicating at what % each level is
  axisGrid
    .selectAll(".axisLabel")
    .data(d3.range(1, cfg.levels + 1).reverse())
    .enter()
    .append("text")
    .attr("class", "axisLabel")
    .attr("x", 4)
    .attr("y", function (d) {
      return (-d * radius) / cfg.levels;
    })
    .attr("dy", "0.4em")
    .style("font-size", fontSize * 0.8 + "px")
    .style("font-family", "Montserrat-Regular")
    .attr("fill", "#737373")
    .text(function (d) {
      return Format((maxValue * d) / cfg.levels);
    });

  // ///////////////////////////////////////////////////////
  // ////////////////// Draw the axes //////////////////////
  // ///////////////////////////////////////////////////////

  // Create the straight lines radiating outward from the center
  var axis = axisGrid
    .selectAll(".axis")
    .data(allAxis)
    .enter()
    .append("g")
    .attr("class", "axis");
  // Append the lines
  axis
    .append("line")
    .attr("x1", 0)
    .attr("y1", 0)
    .attr("x2", function (d, i) {
      return rScale(maxValue * 1.1) * Math.cos(angleSlice * i - Math.PI / 2);
    })
    .attr("y2", function (d, i) {
      return rScale(maxValue * 1.1) * Math.sin(angleSlice * i - Math.PI / 2);
    })
    .attr("class", "line")
    .style("stroke", "white")
    .style("stroke-width", "2px");

  // Append the labels at each axis
  axis
    .append("text")
    .style("font-size", "15px")
    .style("cursor", "pointer")
    .attr("text-anchor", "middle")
    .style("font-family", "Montserrat-Regular")
    .attr("dy", "0rem")
    .attr("x", function (d, i) {
      return (
        rScale((maxValue + 14) * cfg.labelFactor) *
        Math.cos(angleSlice * i - Math.PI / 2)
      );
    })
    .attr("y", function (d, i) {
      return (
        rScale((maxValue + 14) * cfg.labelFactor) *
        Math.sin(angleSlice * i - Math.PI / 2)
      );
    })
    .text(function (d) {
      return i18n.tc(d[0]) + " (" + Format(d[1]) + ")";
    })
    .call(wrap, cfg.wrapWidth, false)
    .on("click", (d) => {
      router.push("/reports/consolidated/" + pillarID + "/" + d[2]);
    })
    .on("mouseover", function (d) {
      var newX = parseFloat(d3.select(this).attr("x"));
      var newY = parseFloat(d3.select(this).attr("y"));
      tooltipBox.transition().duration(200).style("opacity", 1);
      tooltipBox
        .append("text")
        .attr("class", "tooltip-text")
        .attr("stroke", "none")
        .style("fill", "white")
        .style("text-anchor", "middle")
        .style("font-family", "Montserrat-Regular")
        .style("pointer-events", "none")
        .style("font-size", fontSize * 0.9 + "px")
        .attr("x", newX)
        .attr("y", newY)
        .attr("dy", 0)
        .text(i18n.tc(d[3]))
        .call(wrap, 200, true, true);
    })
    .on("mouseout", function () {
      tooltipBox.transition().duration(200).style("opacity", 0);
      tooltipBox.select(".text-background").remove();
      tooltipBox.select(".tooltip-text").remove();
    });

  // ///////////////////////////////////////////////////////
  // /////////// Draw the radar chart blobs ////////////////
  // ///////////////////////////////////////////////////////

  // The radial line function
  var radarLine = d3
    .lineRadial()
    .curve(d3.curveLinearClosed)
    .radius(function (d) {
      return rScale(d.index);
    })
    .angle(function (d, i) {
      return i * angleSlice;
    });

  if (cfg.roundStrokes) {
    radarLine.curve(d3.curveCardinalClosed);
  }

  // Create a wrapper for the blobs
  var blobWrapper = g
    .selectAll(".radarWrapper")
    .data(data)
    .enter()
    .append("g")
    .attr("class", "radarWrapper");

  // Append the backgrounds
  blobWrapper
    .append("path")
    .attr("class", "radarArea")
    .attr("d", function (d) {
      return radarLine(d);
    })
    .style("fill", function (d, i) {
      return cfg.color(i);
    })
    .style("fill-opacity", cfg.opacityArea)
    .on("mouseover", function () {
      // Dim all blobs
      d3.selectAll(".radarArea")
        .transition()
        .duration(200)
        .style("fill-opacity", 0.1);
      // Bring back the hovered over blob
      d3.select(this).transition().duration(200).style("fill-opacity", 0.7);
    })
    .on("mouseout", function () {
      // Bring back all blobs
      d3.selectAll(".radarArea")
        .transition()
        .duration(200)
        .style("fill-opacity", cfg.opacityArea);
    });

  // Create the outlines
  blobWrapper
    .append("path")
    .attr("class", "radarStroke")
    .attr("d", function (d) {
      return radarLine(d);
    })
    .style("stroke-width", cfg.strokeWidth + "px")
    .style("stroke", function (d, i) {
      return cfg.color(i);
    })
    .style("fill", "none")
    .style("filter", "url(#glow)");

  // Append the circles
  blobWrapper
    .selectAll(".radarCircle")
    .data(function (d) {
      return d;
    })
    .enter()
    .append("circle")
    .attr("class", "radarCircle")
    .attr("r", cfg.dotRadius)
    .attr("cx", function (d, i) {
      return rScale(d.index) * Math.cos(angleSlice * i - Math.PI / 2);
    })
    .attr("cy", function (d, i) {
      return rScale(d.index) * Math.sin(angleSlice * i - Math.PI / 2);
    })
    .style("fill", function (d, i, j) {
      return cfg.color(j);
    })
    .style("fill-opacity", 0.8);

  // ///////////////////////////////////////////////////////
  // ////// Append invisible circles for tooltip ///////////
  // ///////////////////////////////////////////////////////

  // Wrapper for the invisible circles on top
  var blobCircleWrapper = g
    .selectAll(".radarCircleWrapper")
    .data(data)
    .enter()
    .append("g")
    .attr("class", "radarCircleWrapper");

  // Append a set of invisible circles on top for the mouseover pop-up
  blobCircleWrapper
    .selectAll(".radarInvisibleCircle")
    .data(function (d) {
      return d;
    })
    .enter()
    .append("circle")
    .attr("class", "radarInvisibleCircle")
    .attr("r", cfg.dotRadius * 1.5)
    .attr("cx", function (d, i) {
      return rScale(d.value) * Math.cos(angleSlice * i - Math.PI / 2);
    })
    .attr("cy", function (d, i) {
      return rScale(d.value) * Math.sin(angleSlice * i - Math.PI / 2);
    })
    .style("fill", "none")
    .style("pointer-events", "all")
    .on("mouseover", function (d) {
      var newX = parseFloat(d3.select(this).attr("cx"));
      var newY = parseFloat(d3.select(this).attr("cy")) + 25;
      tooltipBox.transition().duration(200).style("opacity", 1);
      tooltipBox
        .append("text")
        .attr("class", "tooltip-text")
        .attr("stroke", "none")
        .style("fill", "white")
        .style("text-anchor", "middle")
        .style("font-family", "Montserrat-Regular")
        .attr("x", newX)
        .attr("y", newY)
        .attr("dy", 0)
        .text(Format(d.index))
        .call(wrap, 60, true, true);
    })
    .on("mouseout", function () {
      tooltipBox.transition().duration(200).style("opacity", 0);
      tooltipBox.select(".text-background").remove();
      tooltipBox.select(".tooltip-text").remove();
    });

  var tooltipBox = g
    .append("g")
    .attr("class", "tooltip-box")
    .style("opacity", 0);

  // ///////////////////////////////////////////////////////
  // ///////////////// Helper Function /////////////////////
  // ///////////////////////////////////////////////////////

  // Taken from http://bl.ocks.org/mbostock/7555321
  // Wraps SVG text
  function wrap(text, width, background) {
    // isTooltip = isTooltip || false
    text.each(function () {
      var text = d3.select(this);
      var rect = d3.select(this.parentNode); // Is used in tooltips
      d3.select(this.parentNode).select(".text-background").remove();
      var words = text.text().split(/\s+/).reverse();
      var word;
      var line = [];
      var lines = [];
      var lineNumber = 0;
      var lineHeight = 1; // ems
      var y = text.attr("y");
      var x = text.attr("x");

      var dy = parseFloat(text.attr("dy"));
      var tspan = text
        .text(null)
        .append("tspan")
        .attr("x", x)
        .attr("y", y)
        .attr("dy", dy + "em")
        .attr("z-index", "100");

      while (word = words.pop()) { // eslint-disable-line
        line.push(word);
        tspan.text(line.join(" "));
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(" "));
          lines.push(line);
          line = [word];
          tspan = text
            .append("tspan")
            .attr("x", x)
            .attr("y", y)
            .attr("dy", ++lineNumber * lineHeight + dy + "em")
            .text(word);
        }
      }

      var textBox = tspan.node().getBBox();
      var tBackground = rect.append("rect");
      if (background) {
        tBackground
          .attr("class", "text-background")
          .attr("width", textBox.width + 6)
          .attr("height", textBox.height)
          .style("pointer-events", "none")
          .attr("rx", 5)
          .attr("ry", 5)
          .attr("x", textBox.x - 3)
          .attr("y", textBox.y)
          .style("z-index", 0)
          .attr("fill", "black")
          .lower();
      }

      // Validamos los datos del tooltip para saber si se sale
      // Primero en x
      var deltaX = 0;
      var deltaY = 0;
      if (textBox.x < cfg.x0) {
        deltaX = cfg.x0 - textBox.x + 3;
      } else if (textBox.x + textBox.width > cfg.x1) {
        deltaX = cfg.x1 - (textBox.x + textBox.width) - 3;
      }
      // Luego en Y
      if (textBox.y + textBox.height > cfg.y1) {
        deltaY = cfg.y1 - (textBox.y + textBox.height) - 3;
      }

      d3.select(this.parentNode).attr(
        "transform",
        "translate(" + [deltaX, deltaY] + ")"
      );
    });
  } // wrap
} // RadarChart
